// app/src/api/hospitalSettingApi.ts

import { serverApi } from "@core/api/core"
import { handleApiResponse } from "@core/api/core"
import { createAsyncThunk } from "@reduxjs/toolkit"
import {
  HospitalSetting,
  CreateHospitalSettingRequest,
  UpdateHospitalSettingRequest,
  BetaSettingsData,
  DoctalkReservationSettingsData,
  NaverReservationMemoFieldKey,
  KakaoAlimtalkSettingsData,
  UninsuredDiscountSettingsData,
  normalizeDoctalkReservationSettingsData,
  normalizeUninsuredDiscountSettingsData,
  createDefaultBetaSettingsData,
} from "@/types/hospitalSetting"
import {
  MEMBER_GRANT_PERMISSION_SETTING_NAME,
  MemberGrantPermissionSettingsData,
  normalizeMemberGrantPermissionSettingsData,
} from "@core/constants/memberGrantPermissionSettings"
import { setStoredAutoLogoutDurationMinutes, coerceAutoLogoutDuration } from "@core/utils/autoLogoutDuration"
import type { ReservationExtendSerializer } from "@/types/reservation"
import type { KakaoTemplateVariableSettingsData } from "@/types/kakaoTemplateVariable"
import type { KakaoReceptionTemplateSettingsData, KakaoReservationTemplateSettingsData } from "@/types/kakaoAlimtalkTemplate"

const SECURITY_SETTINGS_ALIASES = ["securitySettings", "security_settings"] as const
const KAKAO_TEMPLATE_VARIABLE_SETTING_NAME = "kakaoAlimtalkVariables"
const KAKAO_RESERVATION_TEMPLATE_SETTING_NAME = "kakaoAlimtalkReservationTemplates"
const KAKAO_RECEPTION_TEMPLATE_SETTING_NAME = "kakaoAlimtalkReceptionTemplate"
const KAKAO_ALIMTALK_SETTING_NAME = "kakaoAlimtalkSettings"

function pickSecuritySettingsRecord(settings: HospitalSetting[]) {
  for (const alias of ["security_settings", "securitySettings"] as const) {
    const matchedSetting = settings.find(setting => setting.hospitalSettingName === alias)
    if (matchedSetting) {
      return matchedSetting
    }
  }

  return settings[0] ?? null
}

function normalizeHospitalSettingName(settingName: string) {
  if (SECURITY_SETTINGS_ALIASES.includes(settingName as (typeof SECURITY_SETTINGS_ALIASES)[number])) {
    return "securitySettings"
  }

  return settingName
}

function normalizeHospitalSetting<T extends HospitalSetting>(setting: T): T {
  const normalizedSetting = setting as T & {
    hospital_setting_name?: string
    hospital_setting_data?: Record<string, any>
    hospital_setting_uuid?: string
    created_dt?: string
    updated_dt?: string
  }

  return {
    ...setting,
    id: setting.id ?? (normalizedSetting as { id?: number }).id,
    hospitalSettingUuid: setting.hospitalSettingUuid ?? normalizedSetting.hospital_setting_uuid ?? "",
    hospitalSettingName: normalizeHospitalSettingName(setting.hospitalSettingName ?? normalizedSetting.hospital_setting_name ?? ""),
    hospitalSettingData: setting.hospitalSettingData ?? normalizedSetting.hospital_setting_data ?? {},
    createdDt: setting.createdDt ?? normalizedSetting.created_dt ?? "",
    updatedDt: setting.updatedDt ?? normalizedSetting.updated_dt ?? "",
  }
}

function syncAutoLogoutDurationFromSettings(settings: HospitalSetting[]) {
  const securitySetting = pickSecuritySettingsRecord(settings)
  const autoLogoutDurationMinutes = coerceAutoLogoutDuration(securitySetting?.hospitalSettingData?.autoLogoutDurationMinutes)

  if (autoLogoutDurationMinutes) {
    setStoredAutoLogoutDurationMinutes(autoLogoutDurationMinutes)
  }
}

// 병원 설정 목록 조회
export const fetchHospitalSettings = createAsyncThunk<HospitalSetting[], void, { rejectValue: string }>(
  "hospitalSetting/fetchHospitalSettings",
  async (_, { rejectWithValue }) => {
    try {
      const requestUrl = `/hospital/public/setting/`
      const response = await serverApi.get(requestUrl)
      const settings = handleApiResponse<HospitalSetting[]>(response).map(normalizeHospitalSetting)
      syncAutoLogoutDurationFromSettings(settings)
      return settings
    } catch (err: any) {
      return rejectWithValue(err.message || "설정 로드 실패")
    }
  }
)

// 특정 설정 이름으로 병원 설정 조회
export const getHospitalSettingByName = async (name: string): Promise<HospitalSetting | null> => {
  const aliases = name === "securitySettings" ? SECURITY_SETTINGS_ALIASES : [name]

  for (const alias of aliases) {
    const requestUrl = `/hospital/public/setting/?hospital_setting_name=${alias}`
    const response = await serverApi.get(requestUrl)
    const rawData = handleApiResponse<HospitalSetting[]>(response)
    const matchedSetting = name === "securitySettings" ? pickSecuritySettingsRecord(rawData) : rawData[0]

    if (matchedSetting) {
      return normalizeHospitalSetting(matchedSetting)
    }
  }

  return null
}

// 병원 설정 생성
export const createHospitalSetting = async (data: CreateHospitalSettingRequest): Promise<HospitalSetting> => {
  const requestUrl = `/hospital/setting/`
  const response = await serverApi.post(requestUrl, data)
  return handleApiResponse<HospitalSetting>(response)
}

// 병원 설정 수정
export const updateHospitalSetting = async (id: number, data: UpdateHospitalSettingRequest): Promise<HospitalSetting> => {
  const requestUrl = `/hospital/setting/${id}/`
  const response = await serverApi.patch(requestUrl, data)
  return handleApiResponse<HospitalSetting>(response)
}

// 병원 설정 삭제
export const deleteHospitalSetting = async (id: number): Promise<void> => {
  const requestUrl = `/hospital/setting/${id}/`
  await serverApi.delete(requestUrl)
}

export const getKakaoTemplateVariableSettings = async (): Promise<KakaoTemplateVariableSettingsData | null> => {
  const setting = await getHospitalSettingByName(KAKAO_TEMPLATE_VARIABLE_SETTING_NAME)
  return setting ? (setting.hospitalSettingData as unknown as KakaoTemplateVariableSettingsData) : null
}

export const saveKakaoTemplateVariableSettings = async (data: KakaoTemplateVariableSettingsData): Promise<KakaoTemplateVariableSettingsData> => {
  const existingSetting = await getHospitalSettingByName(KAKAO_TEMPLATE_VARIABLE_SETTING_NAME)
  const settingData = {
    hospitalSettingName: KAKAO_TEMPLATE_VARIABLE_SETTING_NAME,
    hospitalSettingData: data,
  }
  const savedSetting = existingSetting
    ? await updateHospitalSetting(existingSetting.id, { id: existingSetting.id, ...settingData })
    : await createHospitalSetting(settingData)
  return normalizeHospitalSetting(savedSetting).hospitalSettingData as unknown as KakaoTemplateVariableSettingsData
}

export const getKakaoReservationTemplateSettings = async (): Promise<KakaoReservationTemplateSettingsData | null> => {
  const setting = await getHospitalSettingByName(KAKAO_RESERVATION_TEMPLATE_SETTING_NAME)
  return setting ? (setting.hospitalSettingData as unknown as KakaoReservationTemplateSettingsData) : null
}

export const saveKakaoReservationTemplateSettings = async (
  data: KakaoReservationTemplateSettingsData
): Promise<KakaoReservationTemplateSettingsData> => {
  const existingSetting = await getHospitalSettingByName(KAKAO_RESERVATION_TEMPLATE_SETTING_NAME)
  const settingData = {
    hospitalSettingName: KAKAO_RESERVATION_TEMPLATE_SETTING_NAME,
    hospitalSettingData: data,
  }
  const savedSetting = existingSetting
    ? await updateHospitalSetting(existingSetting.id, { id: existingSetting.id, ...settingData })
    : await createHospitalSetting(settingData)
  return normalizeHospitalSetting(savedSetting).hospitalSettingData as unknown as KakaoReservationTemplateSettingsData
}

export const getKakaoReceptionTemplateSettings = async (): Promise<KakaoReceptionTemplateSettingsData | null> => {
  const setting = await getHospitalSettingByName(KAKAO_RECEPTION_TEMPLATE_SETTING_NAME)
  return setting ? (setting.hospitalSettingData as unknown as KakaoReceptionTemplateSettingsData) : null
}

export const saveKakaoReceptionTemplateSettings = async (data: KakaoReceptionTemplateSettingsData): Promise<KakaoReceptionTemplateSettingsData> => {
  const existingSetting = await getHospitalSettingByName(KAKAO_RECEPTION_TEMPLATE_SETTING_NAME)
  const settingData = {
    hospitalSettingName: KAKAO_RECEPTION_TEMPLATE_SETTING_NAME,
    hospitalSettingData: data,
  }
  const savedSetting = existingSetting
    ? await updateHospitalSetting(existingSetting.id, { id: existingSetting.id, ...settingData })
    : await createHospitalSetting(settingData)
  return normalizeHospitalSetting(savedSetting).hospitalSettingData as unknown as KakaoReceptionTemplateSettingsData
}

export const saveKakaoAlimtalkSettings = async (data: Partial<KakaoAlimtalkSettingsData>): Promise<HospitalSetting> => {
  const existingSetting = await getHospitalSettingByName(KAKAO_ALIMTALK_SETTING_NAME)
  const settingData = {
    hospitalSettingName: KAKAO_ALIMTALK_SETTING_NAME,
    hospitalSettingData: {
      ...(existingSetting?.hospitalSettingData || {}),
      ...data,
    },
  }

  return existingSetting ? updateHospitalSetting(existingSetting.id, { id: existingSetting.id, ...settingData }) : createHospitalSetting(settingData)
}

// 보안 설정 전용 함수들
export const getSecuritySetting = async (): Promise<HospitalSetting | null> => {
  return await getHospitalSettingByName("password_change_interval")
}

export const saveSecuritySetting = async (passwordChangeInterval: string): Promise<HospitalSetting> => {
  const existingSetting = await getSecuritySetting()

  const settingData = {
    hospitalSettingName: "password_change_interval",
    hospitalSettingData: { password_change_interval: passwordChangeInterval },
  }

  if (existingSetting) {
    return await updateHospitalSetting(existingSetting.id, {
      id: existingSetting.id,
      ...settingData,
    })
  } else {
    return await createHospitalSetting(settingData)
  }
}

// 통합 보안 설정 함수들
export const getSecuritySettings = async (): Promise<HospitalSetting | null> => {
  return await getHospitalSettingByName("securitySettings")
}

export interface SecuritySettingsData {
  passwordChangeInterval: string
  autoLogoutDurationMinutes?: number
}

export const saveSecuritySettings = async (securityData: SecuritySettingsData): Promise<HospitalSetting> => {
  const existingSetting = await getSecuritySettings()

  const settingData = {
    hospitalSettingName: "security_settings",
    hospitalSettingData: securityData,
  }

  if (existingSetting) {
    const response = await updateHospitalSetting(existingSetting.id, {
      id: existingSetting.id,
      hospitalSettingData: securityData,
    })
    const normalizedResponse = normalizeHospitalSetting(response)
    const autoLogoutDurationMinutes = coerceAutoLogoutDuration(normalizedResponse.hospitalSettingData?.autoLogoutDurationMinutes)
    if (autoLogoutDurationMinutes) {
      setStoredAutoLogoutDurationMinutes(autoLogoutDurationMinutes)
    }
    return normalizedResponse
  } else {
    const response = await createHospitalSetting(settingData)
    const normalizedResponse = normalizeHospitalSetting(response)
    const autoLogoutDurationMinutes = coerceAutoLogoutDuration(normalizedResponse.hospitalSettingData?.autoLogoutDurationMinutes)
    if (autoLogoutDurationMinutes) {
      setStoredAutoLogoutDurationMinutes(autoLogoutDurationMinutes)
    }
    return normalizedResponse
  }
}

// SMS 설정 함수들
export const getSmsSettings = async (): Promise<HospitalSetting | null> => {
  return await getHospitalSettingByName("smsSettings")
}

export interface SmsSettingsData {
  smsReservedSameDay?: boolean // 예약한날
  smsReservedPreviousDay?: boolean // 예약전일
  smsReservedCurrentDay?: boolean // 예약당일
}

export const saveSmsSetting = async (smsData: SmsSettingsData): Promise<HospitalSetting> => {
  const existingSetting = await getSmsSettings()

  // JSONField 내부도 camelCase로 통일
  const settingData = {
    hospitalSettingName: "smsSettings",
    hospitalSettingData: smsData,
  }

  if (existingSetting) {
    return await updateHospitalSetting(existingSetting.id, {
      id: existingSetting.id,
      ...settingData,
    })
  } else {
    return await createHospitalSetting(settingData)
  }
}

// 채팅 설정 함수들
export const getChatSettings = async (): Promise<HospitalSetting | null> => {
  return await getHospitalSettingByName("chatSettings")
}

export const getBetaSettings = async (): Promise<HospitalSetting | null> => {
  return await getHospitalSettingByName("betaSettings")
}

export interface ChatSettingsData {
  chatEnabled: boolean // 채팅 사용 여부
  chatRetentionMonths?: number // 채팅내역 저장기간 (개월): 1, 3, 6, 12, 36, 60, 120
  chatHistoryPublicEnabled?: boolean // 일반 사용자 과거내역 공개 여부
  chatHistoryPublicDays?: number // 일반 사용자 과거내역 공개기간 (일)
}

export const saveBetaSettings = async (betaData: Partial<BetaSettingsData>): Promise<HospitalSetting> => {
  const existingSetting = await getBetaSettings()

  const settingData = {
    hospitalSettingName: "betaSettings",
    hospitalSettingData: existingSetting ? betaData : { ...createDefaultBetaSettingsData(), ...betaData },
  }

  const writeResult = existingSetting
    ? await updateHospitalSetting(existingSetting.id, { id: existingSetting.id, ...settingData })
    : await createHospitalSetting(settingData)
  const savedSetting = await getBetaSettings()
  if (!savedSetting) {
    throw new Error("저장된 베타버전 설정을 다시 불러오지 못했습니다.")
  }
  return { ...savedSetting, ...(writeResult.cacheReady === undefined ? {} : { cacheReady: writeResult.cacheReady }) }
}

export const getMemberGrantPermissionSettings = async (): Promise<HospitalSetting | null> => {
  return await getHospitalSettingByName(MEMBER_GRANT_PERMISSION_SETTING_NAME)
}

export const saveMemberGrantPermissionSettings = async (permissionData: MemberGrantPermissionSettingsData): Promise<HospitalSetting> => {
  const existingSetting = await getMemberGrantPermissionSettings()
  const normalizedPermissionData = normalizeMemberGrantPermissionSettingsData(permissionData)

  const settingData = {
    hospitalSettingName: MEMBER_GRANT_PERMISSION_SETTING_NAME,
    hospitalSettingData: normalizedPermissionData,
  }

  if (existingSetting) {
    const response = await updateHospitalSetting(existingSetting.id, {
      id: existingSetting.id,
      ...settingData,
    })
    return normalizeHospitalSetting(response)
  }

  const response = await createHospitalSetting(settingData)
  return normalizeHospitalSetting(response)
}

// 여러가지 설정 함수들
export const getMiscSettings = async (): Promise<HospitalSetting | null> => {
  return await getHospitalSettingByName("miscSettings")
}

export type EncounterChartAutoCreateMode = "manual" | "auto" | "primaryDoctorOnly" | "assignedDoctorOnly"
export type ReFirstVisitChangeMode = "default" | "over90Only"
export type MsureEnvironment = "dev" | "prod"
export type DailyClosingPatientSortOrder = "time" | "name"

export interface MsureEnvironmentRequest {
  environment?: MsureEnvironment
}

export interface MsureUrlResult {
  url: string
  environment: MsureEnvironment
  channel: string
}

export interface MsurePatientSilsonUrlRequest extends MsureEnvironmentRequest {
  patientUuid: string
  pagency?: string
}

export interface MsurePatientSilsonUrlResult {
  urlWithoutRn: string
  patientName: string
  patientPhone: string
  environment: MsureEnvironment
  channel: string
}
export type PatientReferrerBadgeIconKey =
  | "star"
  | "dot"
  | "triangle"
  | "diamond"
  | "square"
  | "doubleCircle"
  | "doubleDiamond"
  | "asterisk"
  | "custom"

export interface PatientReferrerBadgeRule {
  minCount?: number
  maxCount?: number
  icon?: PatientReferrerBadgeIconKey
  customSymbol?: string
}

export interface MiscSettingsData {
  reservationTimeUnit: number // 예약시간 단위 (분): 10, 15, 20, 30, 60
  renewalReservationPreferred?: boolean
  naverReservationTimeEnabled?: boolean
  prescriptionAutoAnalysisEnabled?: boolean
  reservationSlotMaxPatientsEnabled?: boolean // 예약시간별 최대 예약환자 수 제한 사용 여부
  reservationSlotMaxPatients?: number // 예약시간별 최대 예약환자 수
  silson24ExcludeTodayEncounters?: boolean // 실손24 검색에서 당일 진료 제외 여부
  autoMoveToTreatmentAfterChartRecord?: boolean // 환자 차트기록 후 자동으로 치료목록 이동 여부
  excludeSeniorHealthInsuranceAutoSurcharge?: boolean // 65세 이상 건강보험 자동가산 설정 제외 여부
  autoAddDiagnosisTechniqueFee?: boolean // 가이드 허용 시 변증기술료 자동 추가 여부
  encounterChartAutoCreateMode?: EncounterChartAutoCreateMode // 차트 자동 생성 방식
  reFirstVisitChangeMode?: ReFirstVisitChangeMode // 재초진 변경 규칙
  msureEnabled?: boolean // 실손보험조회 사용 여부
  msureEnvironment?: MsureEnvironment // 실손보험조회 대상 서버
  cidEnabled?: boolean // CID 연동 사용 여부
  cardTerminalEnabled?: boolean // 신용카드 단말기 연동 사용 여부
  referrerBadgeEnabled?: boolean // 소개환자수 표시 사용 여부
  referrerBadgeRules?: PatientReferrerBadgeRule[] // 소개환자수 표시 다중 구간 규칙
  dailyClosingPatientSortOrder?: DailyClosingPatientSortOrder // 일일결산 환자 정렬 기준
}

export const getUninsuredDiscountSettings = async (): Promise<HospitalSetting | null> => {
  return await getHospitalSettingByName("uninsuredDiscountSettings")
}

export const saveUninsuredDiscountSettings = async (discountData: UninsuredDiscountSettingsData): Promise<HospitalSetting> => {
  const existingSetting = await getUninsuredDiscountSettings()
  const normalizedDiscountData = normalizeUninsuredDiscountSettingsData(discountData)

  const settingData = {
    hospitalSettingName: "uninsuredDiscountSettings",
    hospitalSettingData: normalizedDiscountData,
  }

  if (existingSetting) {
    const response = await updateHospitalSetting(existingSetting.id, {
      id: existingSetting.id,
      ...settingData,
    })
    return normalizeHospitalSetting(response)
  }

  const response = await createHospitalSetting(settingData)
  return normalizeHospitalSetting(response)
}

let miscSettingsSaveQueue: Promise<void> = Promise.resolve()

const enqueueMiscSettingsSave = (operation: () => Promise<void>): Promise<HospitalSetting> => {
  const saved = miscSettingsSaveQueue.then(async () => {
    await operation()
    const setting = await getMiscSettings()
    if (!setting) {
      throw new Error("저장된 여러가지 설정을 다시 불러오지 못했습니다.")
    }
    return setting
  })
  miscSettingsSaveQueue = saved.then(
    () => undefined,
    () => undefined
  )
  return saved
}

export const saveMiscSettings = async (miscData: Partial<MiscSettingsData>): Promise<HospitalSetting> => {
  const changes = { ...miscData }
  return enqueueMiscSettingsSave(async () => {
    const existingSetting = await getMiscSettings()

    const settingData = {
      hospitalSettingName: "miscSettings",
      hospitalSettingData: {
        reservationTimeUnit: 10,
        ...existingSetting?.hospitalSettingData,
        ...changes,
      },
    }

    if (existingSetting) {
      await updateHospitalSetting(existingSetting.id, {
        id: existingSetting.id,
        ...settingData,
      })
    } else {
      await createHospitalSetting(settingData)
    }
  })
}

export const saveDailyClosingPatientSortOrder = async (sortOrder: DailyClosingPatientSortOrder): Promise<HospitalSetting> => {
  return enqueueMiscSettingsSave(async () => {
    const existingSetting = await getMiscSettings()
    const currentData = existingSetting?.hospitalSettingData || {}
    const reservationTimeUnit = Number(currentData.reservationTimeUnit)
    const settingData = {
      hospitalSettingName: "miscSettings",
      hospitalSettingData: {
        ...currentData,
        reservationTimeUnit: Number.isFinite(reservationTimeUnit) && reservationTimeUnit > 0 ? reservationTimeUnit : 10,
        dailyClosingPatientSortOrder: sortOrder,
      },
    }

    if (existingSetting) {
      await updateHospitalSetting(existingSetting.id, {
        id: existingSetting.id,
        ...settingData,
      })
    } else {
      await createHospitalSetting(settingData)
    }
  })
}

export const saveChatSetting = async (chatData: ChatSettingsData): Promise<HospitalSetting> => {
  console.log("[API] saveChatSetting 시작:", chatData)

  const existingSetting = await getChatSettings()
  console.log("[API] 기존 설정:", existingSetting)

  // JSONField 내부도 camelCase로 통일
  const settingData = {
    hospitalSettingName: "chatSettings",
    hospitalSettingData: {
      ...(existingSetting?.hospitalSettingData || {}),
      ...chatData,
    },
  }
  console.log("[API] 저장할 데이터:", settingData)

  if (existingSetting) {
    console.log("[API] 기존 설정 업데이트 (PATCH):", existingSetting.id)
    return await updateHospitalSetting(existingSetting.id, {
      id: existingSetting.id,
      ...settingData,
    })
  } else {
    console.log("[API] 새 설정 생성 (POST)")
    return await createHospitalSetting(settingData)
  }
}

export const getDoctalkReservationSettings = async (): Promise<HospitalSetting | null> => {
  return await getHospitalSettingByName("doctalkReservationSettings")
}

export const saveDoctalkReservationSettings = async (settingsData: DoctalkReservationSettingsData): Promise<HospitalSetting> => {
  const existingSetting = await getDoctalkReservationSettings()
  const normalizedSettingsData = normalizeDoctalkReservationSettingsData(settingsData)
  const { actualHospitalId, emrAccountId, lastFetchedProducts, naverMemoFieldOrder, ...sanitizedSettingsData } = normalizedSettingsData

  const settingData = {
    hospitalSettingName: "doctalkReservationSettings",
    hospitalSettingData: sanitizedSettingsData,
  }

  if (existingSetting) {
    await updateHospitalSetting(existingSetting.id, {
      id: existingSetting.id,
      hospitalSettingData: sanitizedSettingsData,
    })

    const refreshedSetting = await getDoctalkReservationSettings()
    if (refreshedSetting) {
      return normalizeHospitalSetting(refreshedSetting)
    }

    throw new Error("닥톡 예약 연동 설정 저장 후 재조회에 실패했습니다.")
  }

  await createHospitalSetting(settingData)

  const refreshedSetting = await getDoctalkReservationSettings()
  if (refreshedSetting) {
    return normalizeHospitalSetting(refreshedSetting)
  }

  throw new Error("닥톡 예약 연동 설정 생성 후 재조회에 실패했습니다.")
}

export const saveNaverReservationMemoFieldOrder = async (fieldOrder: NaverReservationMemoFieldKey[]): Promise<HospitalSetting> => {
  const existingSetting = await getDoctalkReservationSettings()
  const hospitalSettingData = { naverMemoFieldOrder: fieldOrder }

  if (existingSetting) {
    await updateHospitalSetting(existingSetting.id, {
      id: existingSetting.id,
      hospitalSettingData,
    })
  } else {
    await createHospitalSetting({
      hospitalSettingName: "doctalkReservationSettings",
      hospitalSettingData,
    })
  }

  const refreshedSetting = await getDoctalkReservationSettings()
  if (refreshedSetting) {
    return normalizeHospitalSetting(refreshedSetting)
  }

  throw new Error("네이버 예약 메모 설정 저장 후 재조회에 실패했습니다.")
}

export interface DoctalkRegistrationStatusResult {
  isRegistered: boolean
  registeredAccountId: string
  lastSubmittedEmrAccountId: string
  hospitalName: string
  hospitalId: string
  bizNum: string
  host: string | null
  credentialSource: string
  environment: "dev" | "prod"
  errorCode?: string
  message?: string
}

export interface DoctalkRegistrationUrlResult {
  joinUrl: string
  credentialSource: string
  environment: "dev" | "prod"
}

export interface DoctalkPollingPreviewResult {
  items: Array<Record<string, unknown>>
  count: number
  reservationKeys: string[]
  mode?: "v1" | "v2"
  credentialSource: string
  environment: "dev" | "prod"
  hospitalId: string
}

export interface DoctalkReadPollingResult {
  pollingKeys: string[]
  count: number
  credentialSource: string
  environment: "dev" | "prod"
  hospitalId: string
}

export interface DoctalkImportReservationsResult {
  reservations: ReservationExtendSerializer[]
  count: number
  pollingCount: number
  pollingKeys: string[]
  credentialSource: string
  environment: "dev" | "prod"
  hospitalId: string
}

export interface DoctalkSsoTokenResult {
  authToken: string
  loginUrl: string
  credentialSource: string
  environment: "dev" | "prod"
  hospitalId: string
}

export interface DoctalkSsoTokenValidResult {
  valid: boolean
  token: string
  loginUrl: string
  credentialSource: string
  environment: "dev" | "prod"
  hospitalId: string
}

export interface DoctalkBookingListRequest {
  reservationKey?: string
  startDatetime: string
  endDatetime: string
  searchDateType: "VISIT_DATE" | "CREATE_DATE"
  searchBookingType?: "" | "NORMAL" | "WAITING_QUEUE"
  rowPerPage?: number
  pageNumber?: number
}

export interface DoctalkBookingListResult {
  items: Array<Record<string, unknown>>
  count: number
  isEnd: boolean
  credentialSource: string
  environment: "dev" | "prod"
  hospitalId: string
}

export interface DoctalkBookingDetailResult {
  item: Record<string, unknown>
  credentialSource: string
  environment: "dev" | "prod"
  hospitalId: string
}

export interface DoctalkBookingCreateRequest {
  reservationKey: string
  bookingKey?: string
  bookingType?: "NORMAL" | "WAITING_QUEUE"
  startDatetime: string
  endDatetime: string
  bookerName: string
  bookerPhoneNumber: string
  bookerEmail?: string
  userRequest?: string
  state: "accept" | "receipt"
}

export interface DoctalkBookingCreateResult {
  bookingKey: string
  rawPayload?: Record<string, unknown>
  credentialSource: string
  environment: "dev" | "prod"
  hospitalId: string
}

export interface DoctalkProductItem {
  reservationKey: string
  reservationName: string
  reservationDesc?: string
  bookingConfirmType?: "IMMEDIATE_CONFIRM" | "CONFIRM_AFTER_CHECK" | string
  isVisible?: boolean
}

export interface DoctalkProductListResult {
  items: DoctalkProductItem[]
  count: number
  credentialSource: string
  environment: "dev" | "prod"
  hospitalId: string
}

export interface DoctalkExaminationRoom {
  examinationRoomKey: string
  name: string
}

export interface DoctalkExaminationRoomSyncResult {
  count: number
  examinationRooms: DoctalkExaminationRoom[]
  credentialSource: string
  environment: "dev" | "prod"
  hospitalId: string
}

export type DoctalkBusinessRegistrationType = "" | "GENERAL_TAX_PAYER" | "SUMMARY_TAX_PAYER" | "TAX_FREE_PAYER" | "COOPERATE_PAYER"

export interface DoctalkRegistrationUrlRequestPayload {
  emrAccountId: string
  emrAccountPw: string
  hospitalName: string
  contactPhoneNumber: string
  hospitalPhoneNumber?: string
  institutionNumber: string
  description: string
  doctorName: string
  email?: string
  host?: string
  companyName: string
  repName: string
  bisType?: string
  bisItem?: string
  businessRegistrationNumber: string
  businessRegistrationType?: DoctalkBusinessRegistrationType
}

const DOCTALK_CONNECTION_ACTION_RETRY_COUNT = 1

export interface DoctalkSettingsAccessResult {
  verified: boolean
}

export const getDoctalkRegistrationStatus = async (): Promise<DoctalkRegistrationStatusResult> => {
  const response = await serverApi.get("/hospital/setting/doctalk-registration-status/", {
    "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
  })
  return handleApiResponse<DoctalkRegistrationStatusResult>(response)
}

export const createDoctalkRegistrationUrl = async (payload: DoctalkRegistrationUrlRequestPayload): Promise<DoctalkRegistrationUrlResult> => {
  const response = await serverApi.post("/hospital/setting/doctalk-registration-url/", payload, {
    "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
  })
  return handleApiResponse<DoctalkRegistrationUrlResult>(response)
}

export const createMsureRegistrationUrl = async (payload: MsureEnvironmentRequest = {}): Promise<MsureUrlResult> => {
  const response = await serverApi.post("/msure/registration-url/", payload)
  return handleApiResponse<MsureUrlResult>(response)
}

export const createMsureSubscriptionUrl = async (payload: MsureEnvironmentRequest = {}): Promise<MsureUrlResult> => {
  const response = await serverApi.post("/msure/subscription-url/", payload)
  return handleApiResponse<MsureUrlResult>(response)
}

export const createMsureSilsonListUrl = async (payload: MsureEnvironmentRequest = {}): Promise<MsureUrlResult> => {
  const response = await serverApi.post("/msure/silson-list-url/", payload)
  return handleApiResponse<MsureUrlResult>(response)
}

export const createMsurePatientSilsonUrl = async (payload: MsurePatientSilsonUrlRequest): Promise<MsurePatientSilsonUrlResult> => {
  const response = await serverApi.post("/msure/patient-silson-url/", payload)
  return handleApiResponse<MsurePatientSilsonUrlResult>(response)
}

export const verifyDoctalkSettingsAccess = async (password: string): Promise<DoctalkSettingsAccessResult> => {
  const response = await serverApi.post(
    "/hospital/setting/doctalk-settings-access/",
    { password },
    {
      "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
    }
  )
  return handleApiResponse<DoctalkSettingsAccessResult>(response)
}

export const getDoctalkPollingPreview = async (): Promise<DoctalkPollingPreviewResult> => {
  const response = await serverApi.get("/hospital/setting/doctalk-polling-preview/", {
    "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
  })
  return handleApiResponse<DoctalkPollingPreviewResult>(response)
}

export const getDoctalkPollingPreviewV2 = async (): Promise<DoctalkPollingPreviewResult> => {
  const response = await serverApi.get("/hospital/setting/doctalk-polling-preview-v2/", {
    "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
  })
  return handleApiResponse<DoctalkPollingPreviewResult>(response)
}

export const readDoctalkPolling = async (pollingKeys: string[]): Promise<DoctalkReadPollingResult> => {
  const response = await serverApi.post(
    "/hospital/setting/doctalk-read-polling/",
    { pollingKeys },
    {
      "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
    }
  )
  return handleApiResponse<DoctalkReadPollingResult>(response)
}

export const importDoctalkReservations = async (): Promise<DoctalkImportReservationsResult> => {
  const response = await serverApi.post(
    "/hospital/setting/doctalk-import-reservations/",
    {},
    {
      "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
    }
  )
  return handleApiResponse<DoctalkImportReservationsResult>(response)
}

export const issueDoctalkSsoToken = async (accountId: string, password: string): Promise<DoctalkSsoTokenResult> => {
  const response = await serverApi.post(
    "/hospital/setting/doctalk-sso-token/",
    { accountId, password },
    {
      "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
    }
  )
  return handleApiResponse<DoctalkSsoTokenResult>(response)
}

export const validateDoctalkSsoToken = async (token: string): Promise<DoctalkSsoTokenValidResult> => {
  const response = await serverApi.get("/hospital/setting/doctalk-sso-token-valid/", {
    params: { token },
    "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
  })
  return handleApiResponse<DoctalkSsoTokenValidResult>(response)
}

export const getDoctalkBookingList = async (params: DoctalkBookingListRequest): Promise<DoctalkBookingListResult> => {
  const response = await serverApi.get("/hospital/setting/doctalk-booking-list/", {
    params,
    "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
  })
  return handleApiResponse<DoctalkBookingListResult>(response)
}

export const getDoctalkBookingDetail = async (bookingKey: string): Promise<DoctalkBookingDetailResult> => {
  const response = await serverApi.get("/hospital/setting/doctalk-booking-detail/", {
    params: { bookingKey },
    "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
  })
  return handleApiResponse<DoctalkBookingDetailResult>(response)
}

export const createDoctalkBooking = async (payload: DoctalkBookingCreateRequest): Promise<DoctalkBookingCreateResult> => {
  const response = await serverApi.post("/hospital/setting/doctalk-booking-create/", payload, {
    "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
  })
  return handleApiResponse<DoctalkBookingCreateResult>(response)
}

export const getDoctalkProducts = async (): Promise<DoctalkProductListResult> => {
  const response = await serverApi.get("/hospital/setting/doctalk-products/", {
    "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
  })
  return handleApiResponse<DoctalkProductListResult>(response)
}

export const syncDoctalkExaminationRooms = async (examinationRooms: DoctalkExaminationRoom[]): Promise<DoctalkExaminationRoomSyncResult> => {
  const response = await serverApi.post(
    "/hospital/setting/doctalk-examination-room-sync/",
    { examinationRooms },
    {
      "axios-retry": { retries: DOCTALK_CONNECTION_ACTION_RETRY_COUNT },
    }
  )
  return handleApiResponse<DoctalkExaminationRoomSyncResult>(response)
}

// ========================================
// 진료시간 설정
// ========================================

export interface ClinicHoursItem {
  isOpen: boolean
  startTime: string
  endTime: string
  breakStartTime: string | null
  breakEndTime: string | null
}

export interface ClinicHoursSettingsData {
  sunday: ClinicHoursItem
  monday: ClinicHoursItem
  tuesday: ClinicHoursItem
  wednesday: ClinicHoursItem
  thursday: ClinicHoursItem
  friday: ClinicHoursItem
  saturday: ClinicHoursItem
  holiday: ClinicHoursItem
  nightSurcharge?: NightSurchargeSettings
}

export type ClinicHoursDayKey = Exclude<keyof ClinicHoursSettingsData, "nightSurcharge">

export interface NightSurchargeSettings {
  startTime: string
  mode: "automatic" | "disabled" | "excludeSenior"
}

export const DEFAULT_NIGHT_SURCHARGE_SETTINGS: NightSurchargeSettings = {
  startTime: "18:00",
  mode: "automatic",
}

export const normalizeNightSurchargeSettings = (value: unknown): NightSurchargeSettings => {
  const source = value && typeof value === "object" ? (value as Record<string, unknown>) : {}
  const startTime = source.startTime ?? source.start_time
  const mode = source.mode
  return {
    startTime: typeof startTime === "string" && /^(18|19|2[0-3]):[0-5][0-9]$/.test(startTime) ? startTime : "18:00",
    mode: mode === "disabled" || mode === "excludeSenior" ? mode : "automatic",
  }
}

export const DEFAULT_CLINIC_HOURS_ITEM: ClinicHoursItem = {
  isOpen: false,
  startTime: "09:00",
  endTime: "18:00",
  breakStartTime: null,
  breakEndTime: null,
}

export const DEFAULT_CLINIC_HOURS_SETTINGS: ClinicHoursSettingsData = {
  nightSurcharge: { ...DEFAULT_NIGHT_SURCHARGE_SETTINGS },
  sunday: { ...DEFAULT_CLINIC_HOURS_ITEM },
  monday: { ...DEFAULT_CLINIC_HOURS_ITEM, isOpen: true },
  tuesday: { ...DEFAULT_CLINIC_HOURS_ITEM, isOpen: true },
  wednesday: { ...DEFAULT_CLINIC_HOURS_ITEM, isOpen: true },
  thursday: { ...DEFAULT_CLINIC_HOURS_ITEM, isOpen: true },
  friday: { ...DEFAULT_CLINIC_HOURS_ITEM, isOpen: true },
  saturday: { ...DEFAULT_CLINIC_HOURS_ITEM },
  holiday: { ...DEFAULT_CLINIC_HOURS_ITEM },
}

export interface ClinicHoursByDateResponse {
  date: string
  dayOfWeek: string
  isHoliday: boolean
  holidayName: string | null
  appliedType: string
  hasClinicHoursSettings: boolean
  clinicHours: ClinicHoursItem
  availableRanges: Array<{
    startTime: string
    endTime: string
  }> | null
}

// 진료시간 설정 조회
export const getClinicHoursSettings = async (): Promise<HospitalSetting | null> => {
  return await getHospitalSettingByName("clinicHoursSettings")
}

// 진료시간 설정 저장
export const saveClinicHoursSettings = async (clinicHoursData: ClinicHoursSettingsData): Promise<HospitalSetting> => {
  const existingSetting = await getClinicHoursSettings()

  const settingData = {
    hospitalSettingName: "clinicHoursSettings",
    hospitalSettingData: clinicHoursData,
  }

  if (existingSetting) {
    return await updateHospitalSetting(existingSetting.id, {
      id: existingSetting.id,
      ...settingData,
    })
  } else {
    return await createHospitalSetting(settingData)
  }
}

// 날짜별 진료시간 조회
export const getClinicHoursByDate = async (date: string): Promise<ClinicHoursByDateResponse> => {
  const response = await serverApi.get("/hospital/public/setting/clinic-hours/", { params: { date } })
  return handleApiResponse<ClinicHoursByDateResponse>(response)
}
